import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConnectionService } from './connection.service';
import { SwalService } from './swal.service';
import { iconSvg } from '../components/icon/icon.component';

// Stato notifiche condiviso: un solo polling per tutta l'app,
// riusato sia dalla campanella desktop (nav-bar) sia dal pannello mobile (bottom-tab-bar).
@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
  private connection = inject(ConnectionService);
  private swal = inject(SwalService);
  private router = inject(Router);

  notifications: any[] = [];
  unreadCount = 0;
  loading = false;

  private loaded = false;
  private pollInterval = setInterval(() => this.fetch(), 2 * 60 * 1000);

  constructor() {
    this.fetch();
  }

  // Cambio utente nella stessa scheda: lo stato appartiene alla sessione precedente
  reset(): void {
    this.notifications = [];
    this.unreadCount = 0;
    this.loading = false;
    this.loaded = false;
  }

  ngOnDestroy(): void {
    clearInterval(this.pollInterval);
  }

  fetch(): void {
    this.connection.get(
      '@notifications',
      (data: any[]) => {
        this.notifications = data ?? [];
        this.unreadCount = this.notifications.filter((n) => !n.read).length;
        this.loaded = true;
        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  ensureLoaded(): void {
    if (this.loaded) return;
    this.loading = true;
    this.fetch();
  }

  handleInvite(notif: any): void {
    this.swal
      .confirmHtml(
        'Invito collaborazione',
        this.buildInviteHtml(notif),
        'Accetta',
        'Rifiuta',
      )
      .then((result) => {
        const accept = result.isConfirmed;
        this.connection.post(
          `events/${notif.data.event_id}/invite/respond`,
          { accept },
          () => {
            this.connection.put(
              `notifications/${notif._id}/read`,
              {},
              () => {
                notif.read = true;
                this.unreadCount = Math.max(0, this.unreadCount - 1);
                this.swal.success(
                  accept ? 'Collaborazione accettata!' : 'Invito rifiutato',
                );
              },
              () => {},
            );
          },
          () => {},
        );
      });
  }

  private buildInviteHtml(notif: any): string {
    const d = notif.data ?? {};
    const rows: string[] = [];
    if (d.owner_name) {
      rows.push(`<div style="margin-bottom:6px"><strong>${d.owner_name}</strong> ti invita a collaborare.</div>`);
    }
    if (d.client_name) rows.push(`<div>${iconSvg('user')} Cliente: <strong>${d.client_name}</strong></div>`);
    if (d.event_date) rows.push(`<div>${iconSvg('calendar')} Data: <strong>${d.event_date}</strong></div>`);
    if (d.event_location) rows.push(`<div>${iconSvg('pin')} Luogo: <strong>${d.event_location}</strong></div>`);

    if (!rows.length) return notif.message ?? '';
    return `<div style="text-align:left;font-size:0.95rem;line-height:1.6">${rows.join('')}</div>`;
  }

  markRead(notif: any, onNavigate: (notif: any) => void): void {
    if (
      notif.type === 'invito_collaborazione' &&
      !notif.read &&
      notif.data?.event_id &&
      notif.data?.owner_user_id
    ) {
      this.handleInvite(notif);
      return;
    }

    if (notif.read) {
      onNavigate(notif);
      return;
    }

    this.connection.put(
      `notifications/${notif._id}/read`,
      {},
      () => {
        notif.read = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        onNavigate(notif);
      },
      () => {},
    );
  }

  markAllRead(): void {
    this.connection.put(
      'notifications/read-all',
      {},
      () => {
        this.notifications.forEach((n) => (n.read = true));
        this.unreadCount = 0;
      },
      () => {},
    );
  }

  navigateToNotif(notif: any): void {
    if (notif.data?.event_id) {
      this.router.navigate(['/evento', notif.data.event_id]);
    }
  }

  getNotifIcon(type: string): string {
    const icons: Record<string, string> = {
      matrimonio_imminente: 'rings',
      promemoria_prova: 'scissors',
      deposito_non_pagato: 'euro',
      nuovo_contratto: 'file',
      preventivo_accettato: 'check',
      invito_collaborazione: 'users',
    };
    return icons[type] ?? 'bell';
  }
}
